import React, { useEffect, useRef, useState } from 'react';
import { useWindowMouseDrag } from '~/hooks/useWindowMouseDrag';

/** Pointer travel per event that counts as a drag, in pixels. */
const DRAG_THRESHOLD_PX = 2;

/** Amount one drag step changes the value by. */
const NUDGE_STEP = 3;

interface VerticalNudgeOptions {
    /**
     * Applied once per drag step, positive when the pointer moves up. The
     * caller decides which quantity the step belongs to.
     */
    onNudge: (delta: number) => void;
    /** A press that never travelled far enough to nudge. */
    onClick: () => void;
    /** When true the press is ignored, so an open editor keeps the pointer. */
    disabled?: boolean;
}

interface VerticalNudge {
    onMouseDown: (event: React.MouseEvent) => void;
    /** True between the press and the release of a drag. */
    isDragging: boolean;
}

/**
 * Vertical drag-to-nudge on a numeric readout, with a click that survives it.
 *
 * The pointer is locked for the drag, so the value keeps changing past the
 * edge of the screen. Travel below a small threshold is ignored, and a press
 * that never crossed it reports a click instead: one
 * element can carry both the drag and a click-to-edit affordance without the
 * two firing together.
 */
export function useVerticalNudge({
    onNudge,
    onClick,
    disabled = false,
}: VerticalNudgeOptions): VerticalNudge {
    const [isDragging, setIsDragging] = useState(false);
    const hasNudgedRef = useRef(false);
    const lockedElementRef = useRef<Element | null>(null);

    // Gives the pointer back only while this control holds it. Another
    // control's lock, such as the canvas windowing tool's, is left alone.
    const releasePointerLock = (): void => {
        if (
            lockedElementRef.current !== null &&
            document.pointerLockElement === lockedElementRef.current
        ) {
            document.exitPointerLock?.();
        }
        lockedElementRef.current = null;
    };
    const releasePointerLockRef = useRef(releasePointerLock);
    releasePointerLockRef.current = releasePointerLock;

    // The lock is taken on the press and given back on the release. An
    // element that goes away mid-drag never sees its release, so the capture
    // is handed back here as well: nothing may leave the pointer held by a
    // control the user can no longer see.
    useEffect(() => () => releasePointerLockRef.current(), []);

    const startDrag = useWindowMouseDrag(
        (event) => {
            const deltaY = event.movementY;
            if (Math.abs(deltaY) < DRAG_THRESHOLD_PX) {
                return;
            }
            hasNudgedRef.current = true;
            onNudge(deltaY < 0 ? NUDGE_STEP : -NUDGE_STEP);
        },
        () => {
            setIsDragging(false);
            releasePointerLock();
            if (!hasNudgedRef.current) {
                onClick();
            }
        }
    );

    const onMouseDown = (event: React.MouseEvent): void => {
        if (disabled) {
            return;
        }
        setIsDragging(true);
        hasNudgedRef.current = false;
        const target = event.currentTarget as HTMLElement;
        lockedElementRef.current = target;
        target.requestPointerLock?.();
        startDrag(event);
    };

    return { onMouseDown, isDragging };
}
